import React, { useState, useEffect, useCallback } from 'react';
import partnerApi from '../../api/partnerApi';
import { Plus, Search, Phone, Mail, MapPin, Users, ChevronRight, X, ArrowLeft, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'all',            label: 'All Leads',       color: 'text-slate-600',   bg: '' },
  { key: 'prospect',       label: 'Prospect',         color: 'text-slate-500',   bg: 'bg-slate-100 text-slate-600' },
  { key: 'contacted',      label: 'Contacted',        color: 'text-blue-600',    bg: 'bg-blue-100 text-blue-700' },
  { key: 'demo_scheduled', label: 'Demo Scheduled',   color: 'text-purple-600',  bg: 'bg-purple-100 text-purple-700' },
  { key: 'proposal_sent',  label: 'Proposal Sent',    color: 'text-indigo-600',  bg: 'bg-indigo-100 text-indigo-700' },
  { key: 'negotiating',    label: 'Negotiating',      color: 'text-amber-600',   bg: 'bg-amber-100 text-amber-700' },
  { key: 'won',            label: 'Won',              color: 'text-emerald-600', bg: 'bg-emerald-100 text-emerald-700' },
  { key: 'lost',           label: 'Lost',             color: 'text-red-500',     bg: 'bg-red-100 text-red-600' },
];

const ACTIVITY_TYPES = [
  { key: 'call',      label: 'Call',      icon: '📞' },
  { key: 'email',     label: 'Email',     icon: '✉️' },
  { key: 'meeting',   label: 'Meeting',   icon: '🤝' },
  { key: 'whatsapp',  label: 'WhatsApp',  icon: '💬' },
  { key: 'demo',      label: 'Demo',      icon: '🖥️' },
  { key: 'note',      label: 'Note',      icon: '📝' },
];

const getStageMeta = (key) => STAGES.find(s => s.key === key) || STAGES[1];

const EMPTY_LEAD = {
  school_name: '', contact_name: '', contact_email: '', contact_phone: '',
  school_location: '', school_type: '', est_student_count: 0,
  status: 'prospect', stage_notes: '', expected_close_date: '',
};

export default function PartnerLeads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState('all');
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [creating, setCreating] = useState(false);

  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [newActivity, setNewActivity] = useState({ type: 'call', notes: '', follow_up_date: '' });
  const [addingActivity, setAddingActivity] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);

  const [editingStatus, setEditingStatus] = useState(null);

  const load = useCallback((stage) => {
    setLoading(true);
    const params = stage && stage !== 'all' ? `?status=${stage}` : '';
    partnerApi.get(`/leads${params}`)
      .then((res) => {
        if (res?.success) {
          setLeads(res.data?.leads || []);
          setTotal(res.data?.total || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(filterStage); }, [filterStage, load]);

  const loadDetail = async (lead) => {
    setSelected(lead);
    setDetailLoading(true);
    setActivities([]);
    try {
      const res = await partnerApi.get(`/leads/${lead.id}`);
      if (res?.success) {
        setSelected(res.data.lead);
        setActivities(res.data.activities || []);
      }
    } catch (_) {}
    finally { setDetailLoading(false); }
  };

  const createLead = async () => {
    if (!newLead.school_name.trim()) { toast.error('School name is required'); return; }
    setCreating(true);
    try {
      const res = await partnerApi.post('/leads', {
        ...newLead,
        est_student_count: parseInt(newLead.est_student_count) || 0,
      });
      if (res?.success) {
        toast.success('Lead added');
        setShowCreate(false);
        setNewLead(EMPTY_LEAD);
        load(filterStage);
      }
    } catch (err) {
      toast.error(err?.detail || 'Failed to create lead');
    } finally { setCreating(false); }
  };

  const changeStatus = async (leadId, status) => {
    try {
      await partnerApi.put(`/leads/${leadId}`, { status });
      toast.success('Status updated');
      load(filterStage);
      if (selected?.id === leadId) setSelected(s => ({ ...s, status }));
      setEditingStatus(null);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const addActivity = async () => {
    if (!newActivity.notes.trim()) { toast.error('Notes are required'); return; }
    setAddingActivity(true);
    try {
      const res = await partnerApi.post(`/leads/${selected.id}/activities`, {
        type: newActivity.type,
        notes: newActivity.notes,
        follow_up_date: newActivity.follow_up_date || null,
      });
      if (res?.success) {
        toast.success('Activity logged');
        setActivities(prev => [res.data, ...prev]);
        setNewActivity({ type: 'call', notes: '', follow_up_date: '' });
        setShowActivityForm(false);
      }
    } catch (err) {
      toast.error(err?.detail || 'Failed to log activity');
    } finally { setAddingActivity(false); }
  };

  const archiveLead = async (leadId) => {
    try {
      await partnerApi.delete(`/leads/${leadId}`);
      toast.success('Lead archived');
      load(filterStage);
      if (selected?.id === leadId) setSelected(null);
    } catch (err) {
      toast.error('Failed to archive');
    }
  };

  const filtered = search
    ? leads.filter(l =>
        l.school_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.school_location?.toLowerCase().includes(search.toLowerCase())
      )
    : leads;

  // Stage counts
  const [stageCounts, setStageCounts] = useState({});
  useEffect(() => {
    partnerApi.get('/leads').then(res => {
      if (res?.success) {
        const counts = {};
        (res.data?.leads || []).forEach(l => {
          counts[l.status] = (counts[l.status] || 0) + 1;
        });
        setStageCounts(counts);
      }
    }).catch(() => {});
  }, [leads]);

  return (
    <div className="flex h-full" data-testid="partner-leads-page">
      {/* Main panel */}
      <div className={`flex-1 flex flex-col min-w-0 ${selected ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-outfit font-semibold text-slate-900">Leads Pipeline</h1>
              <p className="text-sm text-slate-500 mt-0.5">{total} total lead{total !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => { setShowCreate(true); setSelected(null); }}
              className="flex items-center gap-2 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4" /> Add Lead
            </button>
          </div>

          {/* Stage filter tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
            {STAGES.map((s) => (
              <button
                key={s.key}
                onClick={() => setFilterStage(s.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  filterStage === s.key
                    ? 'bg-[#516EFA] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`stage-filter-${s.key}`}
              >
                {s.label}
                {s.key !== 'all' && stageCounts[s.key] > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filterStage === s.key ? 'bg-white/20' : 'bg-slate-200'}`}>
                    {stageCounts[s.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads..."
              className="w-full pl-9 border border-slate-200 rounded-lg py-2 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
              data-testid="leads-search"
            />
          </div>

          {/* Add Lead Form */}
          {showCreate && (
            <div className="bg-white border border-[#516EFA]/20 rounded-2xl p-5 shadow-sm space-y-4" data-testid="create-lead-form">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 text-sm">Add New Lead</p>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-400" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">School Name *</label>
                  <input value={newLead.school_name} onChange={e => setNewLead(p => ({ ...p, school_name: e.target.value }))}
                    placeholder="Greenfield Academy"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="lead-school-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Contact Name</label>
                  <input value={newLead.contact_name} onChange={e => setNewLead(p => ({ ...p, contact_name: e.target.value }))}
                    placeholder="Principal Adeyemi"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="lead-contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Phone</label>
                  <input value={newLead.contact_phone} onChange={e => setNewLead(p => ({ ...p, contact_phone: e.target.value }))}
                    placeholder="08012345678"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="lead-contact-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Email</label>
                  <input type="email" value={newLead.contact_email} onChange={e => setNewLead(p => ({ ...p, contact_email: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Location</label>
                  <input value={newLead.school_location} onChange={e => setNewLead(p => ({ ...p, school_location: e.target.value }))}
                    placeholder="Ikeja, Lagos"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="lead-location"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Est. Students</label>
                  <input type="number" min="0" value={newLead.est_student_count} onChange={e => setNewLead(p => ({ ...p, est_student_count: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    data-testid="lead-students"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Pipeline Stage</label>
                  <select value={newLead.status} onChange={e => setNewLead(p => ({ ...p, status: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] bg-white"
                    data-testid="lead-status-select"
                  >
                    {STAGES.filter(s => s.key !== 'all').map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Expected Close</label>
                  <input type="date" value={newLead.expected_close_date} onChange={e => setNewLead(p => ({ ...p, expected_close_date: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Notes</label>
                  <textarea value={newLead.stage_notes} onChange={e => setNewLead(p => ({ ...p, stage_notes: e.target.value }))}
                    rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowCreate(false)} className="border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
                <button onClick={createLead} disabled={creating}
                  className="bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-lead-btn"
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  {creating ? 'Adding...' : 'Add Lead'}
                </button>
              </div>
            </div>
          )}

          {/* Leads table */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">School</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Contact</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Location</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Stage</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Activities</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center">
                      <p className="text-slate-400 text-sm">No leads yet. Add your first school contact!</p>
                    </td>
                  </tr>
                )}
                {filtered.map((lead) => {
                  const stage = getStageMeta(lead.status);
                  return (
                    <tr
                      key={lead.id}
                      className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer"
                      onClick={() => loadDetail(lead)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-slate-800 text-sm">{lead.school_name}</p>
                        {lead.est_student_count > 0 && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Users className="w-3 h-3" /> {lead.est_student_count} students
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <p className="text-slate-700 text-xs">{lead.contact_name || '—'}</p>
                        {lead.contact_phone && (
                          <p className="text-xs text-slate-400">{lead.contact_phone}</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-500 hidden lg:table-cell">
                        {lead.school_location || '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${stage.bg}`}>
                          {stage.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <MessageSquare className="w-3.5 h-3.5" />
                          {lead.activity_count ?? 0}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="w-full lg:w-[380px] flex-shrink-0 bg-white border-l border-slate-200 flex flex-col h-full overflow-y-auto" data-testid="lead-detail-panel">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-slate-100 rounded-lg lg:hidden">
              <ArrowLeft className="w-4 h-4 text-slate-500" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{selected.school_name}</p>
              <p className="text-xs text-slate-400">{selected.school_location || 'No location'}</p>
            </div>
            <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-slate-100 rounded-lg hidden lg:block">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          <div className="p-5 space-y-4 flex-1">
            {detailLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#516EFA] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Stage selector */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pipeline Stage</p>
                  {editingStatus === selected.id ? (
                    <div className="flex flex-wrap gap-1.5">
                      {STAGES.filter(s => s.key !== 'all').map(s => (
                        <button
                          key={s.key}
                          onClick={() => changeStatus(selected.id, s.key)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${s.bg || 'bg-slate-100 text-slate-600'} hover:ring-2 hover:ring-[#516EFA]/30`}
                        >
                          {s.label}
                        </button>
                      ))}
                      <button onClick={() => setEditingStatus(null)} className="px-2 py-1 rounded-lg text-xs text-slate-500 hover:bg-slate-100">Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingStatus(selected.id)}
                      className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[#516EFA] transition-colors ${getStageMeta(selected.status).bg}`}
                      data-testid="change-stage-btn"
                    >
                      {getStageMeta(selected.status).label}
                      <ChevronRight className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                </div>

                {/* Contact info */}
                <div className="space-y-2 text-sm">
                  {selected.contact_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold">{selected.contact_name[0]}</span>
                      </div>
                      {selected.contact_name}
                    </div>
                  )}
                  {selected.contact_phone && (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Phone className="w-3.5 h-3.5" /> {selected.contact_phone}
                    </div>
                  )}
                  {selected.contact_email && (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Mail className="w-3.5 h-3.5" /> {selected.contact_email}
                    </div>
                  )}
                  {selected.school_location && (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <MapPin className="w-3.5 h-3.5" /> {selected.school_location}
                    </div>
                  )}
                  {selected.est_student_count > 0 && (
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Users className="w-3.5 h-3.5" /> ~{selected.est_student_count} students
                    </div>
                  )}
                </div>

                {selected.stage_notes && (
                  <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">{selected.stage_notes}</div>
                )}

                {selected.expected_close_date && (
                  <p className="text-xs text-slate-500">Expected close: {new Date(selected.expected_close_date).toLocaleDateString()}</p>
                )}

                {/* Activity log */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Activity Log</p>
                    <button
                      onClick={() => setShowActivityForm(v => !v)}
                      className="text-xs text-[#516EFA] hover:text-[#425BE0] font-medium flex items-center gap-1"
                      data-testid="log-activity-btn"
                    >
                      <Plus className="w-3.5 h-3.5" /> Log Activity
                    </button>
                  </div>

                  {showActivityForm && (
                    <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-3 mb-3 space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {ACTIVITY_TYPES.map(at => (
                          <button
                            key={at.key}
                            onClick={() => setNewActivity(p => ({ ...p, type: at.key }))}
                            className={`text-xs px-2 py-1 rounded-lg font-medium transition-colors ${
                              newActivity.type === at.key ? 'bg-[#516EFA] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#516EFA]'
                            }`}
                            data-testid={`activity-type-${at.key}`}
                          >
                            {at.icon} {at.label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={newActivity.notes}
                        onChange={e => setNewActivity(p => ({ ...p, notes: e.target.value }))}
                        placeholder="What happened? What's next?"
                        rows={2}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-[#516EFA] resize-none"
                        data-testid="activity-notes"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={newActivity.follow_up_date}
                          onChange={e => setNewActivity(p => ({ ...p, follow_up_date: e.target.value }))}
                          className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#516EFA]"
                          placeholder="Follow-up date"
                        />
                        <button
                          onClick={addActivity}
                          disabled={addingActivity}
                          className="bg-[#516EFA] text-white rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50 flex items-center gap-1"
                          data-testid="save-activity-btn"
                        >
                          {addingActivity ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          Save
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {activities.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-3">No activities logged yet</p>
                    )}
                    {activities.map(a => {
                      const at = ACTIVITY_TYPES.find(t => t.key === a.type) || { icon: '📝', label: a.type };
                      return (
                        <div key={a.id} className="flex gap-2.5 text-xs" data-testid={`activity-${a.id}`}>
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                            {at.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="font-medium text-slate-700 capitalize">{at.label}</span>
                              <span className="text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-slate-500">{a.notes}</p>
                            {a.follow_up_date && (
                              <p className="text-amber-600 mt-0.5">Follow up: {new Date(a.follow_up_date).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Archive */}
                <button
                  onClick={() => archiveLead(selected.id)}
                  className="w-full text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg py-2 transition-colors border border-transparent hover:border-red-100"
                  data-testid="archive-lead-btn"
                >
                  Archive Lead
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
